'use client';

import React, { useState, useEffect, useCallback } from 'react';
import './OrganisationDetail.css';
import { Box, Tab, Tabs, Typography, CircularProgress } from '@mui/material';
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import Layout from '../../../../components/Layout';
import AdminNavbar from '../../../../components/Admin/AdminNavbar/AdminNavbar';
import PropTypes from 'prop-types';
import OrganizationInstructor from '../../../../components/Admin/Organisation/OrganizationInstructor/OrganizationInstructor';
import { axiosInstance } from '../../../../utils/tokenUtils';
import { toast } from 'react-hot-toast';

import OrganizationOverview from '../../../../components/Admin/Organisation/OrganizationOverview/OrganizationOverview';
import OrganizationEarning from '../../../../components/Admin/Organisation/OrganizationEarning/OrganizationEarning';
import OrganizationPayout from '../../../../components/Admin/Organisation/OrganizationPayout/OrganizationPayout';

// Tab panel component
function CustomTabPanel(props) {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`simple-tabpanel-${index}`}
      aria-labelledby={`simple-tab-${index}`}
      {...other}
    >
      {value === index && <Box className="tab-content-org">{children}</Box>}
    </div>
  );
}

CustomTabPanel.propTypes = {
  children: PropTypes.node,
  index: PropTypes.number.isRequired,
  value: PropTypes.number.isRequired,
};

function a11yProps(index) {
  return {
    id: `simple-tab-${index}`,
    'aria-controls': `simple-tabpanel-${index}`,
  };
}

const OrganisationDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const [loading, setLoading] = useState(true);
  const [organisationData, setOrganisationData] = useState(null);
  const [rbacMe, setRbacMe] = useState(null);

  // Get the initial tab value from URL search params or session storage
  const getInitialTabValue = () => {
    // First check URL search params
    const searchParams = new URLSearchParams(location.search);
    const tabParam = searchParams.get('tab');
    if (tabParam !== null) {
      const tabValue = Number.parseInt(tabParam, 10);
      return isNaN(tabValue) ? 0 : tabValue;
    }

    // Then check session storage
    const savedTab = sessionStorage.getItem(`organisationTab-${id}`);
    if (savedTab !== null) {
      const tabValue = Number.parseInt(savedTab, 10);
      return isNaN(tabValue) ? 0 : tabValue;
    }

    // Default to 0 (Overview)
    return 0;
  };

  const [value, setValue] = useState(getInitialTabValue());

  const handleChange = (event, newValue) => {
    setValue(newValue);
    // Save the current tab to session storage for persistence
    sessionStorage.setItem(`organisationTab-${id}`, newValue.toString());
    // Also update URL without navigating
    const searchParams = new URLSearchParams(location.search);
    searchParams.set('tab', newValue.toString());
    navigate(`${location.pathname}?${searchParams.toString()}`, { replace: true });
  };

  const fetchOrganisationDetails = useCallback(async () => {
    try {
      setLoading(true);
      const { data } = await axiosInstance.get(`/admin/organisations/${id}`);
      setOrganisationData(data);
    } catch (error) {
      console.error('Error fetching organisation details:', error);
      toast.error('Failed to fetch organisation details.');
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => {
    fetchOrganisationDetails();
  }, [fetchOrganisationDetails]);

  useEffect(() => {
    const fetchCurrentUser = async () => {
      try {
        let cachedData = null;
        try {
          const cached = sessionStorage.getItem('rbac_admin_me');
          if (cached) {
            cachedData = JSON.parse(cached);
            setRbacMe(cachedData);
          }
        } catch (_) {}

        const { data } = await axiosInstance.get('/admin/rbac/admin/me');
        setRbacMe(data);

        try {
          sessionStorage.setItem('rbac_admin_me', JSON.stringify(data));
        } catch (_) {}
      } catch (_) {
        if (!rbacMe) setRbacMe({ roles: [], permissions: [] });
      }
    };

    fetchCurrentUser();
  }, []);

  const hasPermission = permission => {
    if (!rbacMe || !Array.isArray(rbacMe.permissions)) return false;
    return rbacMe.permissions.includes(permission);
  };

  const canManageOrganisations = () => {
    return (
      hasPermission('manage_organisations') ||
      hasPermission('manage_bank_details') ||
      (rbacMe?.roles || []).includes('super_admin')
    );
  };

  const navigateToOrganisationList = () => {
    navigate('/admin/organisation');
  };

  if (loading) {
    return (
      <Box
        sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}
      >
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Layout title="Klass Online | Organisation Details">
      <Box className="relative h-screen w-full">
        <Box className="sticky top-0 left-0 right-0 z-[1000] bg-white pr-[10px]">
          <AdminNavbar />
        </Box>

        <Box className="organisation-detail-container-org">
          <Box className="organisation-content-org">
            <Box className="organisation-header-org">
              <Box>
                <Box className="flex items-center gap-2">
                  <Typography className="breadcrumb-text-org" onClick={navigateToOrganisationList}>
                    Organization {'>'} <span className="profile-text-org">Profile</span>
                  </Typography>
                </Box>
                <Typography className="organisation-name-text-org">
                  {organisationData?.name}
                </Typography>
              </Box>
            </Box>

            <Box className="organisation-tab-container-org">
              <Box sx={{ width: '100%' }}>
                <Box sx={{ borderBottom: 1, borderColor: '#E5E7EB' }}>
                  <Tabs
                    value={value}
                    onChange={handleChange}
                    aria-label="organisation tabs"
                    className="organisation-tabs-org"
                    sx={{
                      '& .MuiTab-root': {
                        textTransform: 'none',
                        fontFamily: 'Poppins',
                        fontSize: '14px',
                        fontWeight: 500,
                        lineHeight: '20px',
                        color: '#949DA6',
                        minHeight: '48px',
                        padding: '12px 0',
                        marginRight: '32px',
                      },
                      '& .Mui-selected': {
                        color: '#0A5EF3 !important',
                      },
                      '& .MuiTabs-indicator': {
                        backgroundColor: '#0A5EF3',
                        height: '2px',
                      },
                    }}
                  >
                    <Tab label="Overview" {...a11yProps(0)} />
                    <Tab label="Instructors" {...a11yProps(1)} />
                    <Tab label="Earnings" {...a11yProps(2)} />
                    <Tab label="Payout" {...a11yProps(3)} />
                  </Tabs>
                </Box>

                <CustomTabPanel value={value} index={0}>
                  <OrganizationOverview
                    organisationData={organisationData}
                    canManageOrganisations={canManageOrganisations()}
                  />
                </CustomTabPanel>
                <CustomTabPanel value={value} index={1}>
                  <OrganizationInstructor
                    organizationId={id}
                    canManageOrganisations={canManageOrganisations()}
                  />
                </CustomTabPanel>
                <CustomTabPanel value={value} index={2}>
                  <OrganizationEarning organizationId={id} />
                </CustomTabPanel>
                <CustomTabPanel value={value} index={3}>
                  <OrganizationPayout organizationId={id} />
                </CustomTabPanel>
              </Box>
            </Box>
          </Box>
        </Box>
      </Box>
    </Layout>
  );
};

export default OrganisationDetail;
